import {
  animate,
  AnimationEvent,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthState,
  ConfigService,
  CoreCounterState,
  DelegationActions,
  DelegationState,
  LoadingState,
  NotificationState,
  OrgConfiguration,
  SignIn,
  SignOut,
  UserPreferencesActions,
  UserPreferencesState,
  UserState,
} from '@osos/shared/legacy-core';
import {
  AccessRightLevelEnum,
  DelegationPresenterModel,
} from '@osos/shared/types';
import { ComponentBase } from '@osos/shared/utility';
import { fromEvent, merge, Observable } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { MainComponent } from '../main/main.component';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  animations: [
    trigger('topbarActionPanelAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.8)' }),
        animate(
          '.12s cubic-bezier(0, 0, 0.2, 1)',
          style({ opacity: 1, transform: '*' }),
        ),
      ]),
      transition(':leave', [animate('.1s linear', style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class TopbarComponent extends ComponentBase implements OnInit {
  activeDelegation: DelegationPresenterModel;
  activeItem: number;
  showSearch = false;

  accessRightLevels = AccessRightLevelEnum;
  isMobileScreen = false;

  @ViewChild('searchInput') searchInputViewChild: ElementRef;
  @ViewChild('searchMenu') searchMenu: ElementRef;
  @ViewChild('notificationMenu') notificationMenu: ElementRef;
  @Input() clickHamburger: boolean;

  loading$: Observable<boolean>;
  commandLoading$: Observable<boolean>;
  counterLoading$: Observable<boolean>;
  // very important to get user info after signin
  isLoggedIn$: Observable<boolean>;
  unreadCount$: Observable<number>;
  tourSupported$: Observable<boolean>;

  showNotification: boolean;

  userInfo: any;

  showAISide: boolean;
  get orgConfig(): OrgConfiguration {
    return this.config.orgConfig;
  }

  get isOsosAIEnabled(): boolean {
    return this.config.configuration?.IS_OSOS_AI_ENABLED ?? false;
  }

  constructor(
    public appMain: MainComponent,
    private store: Store,
    private config: ConfigService,
    private breakpointObserver: BreakpointObserver,
  ) {
    super();
    this.loading$ = this.store.select(LoadingState.loading);
    this.commandLoading$ = this.store.select(LoadingState.commandLoading);
    this.counterLoading$ = this.store.select(CoreCounterState.loading);
    this.unreadCount$ = this.store.select(NotificationState.unreadCount);
    this.isLoggedIn$ = this.store.select(AuthState.isAuthenticated);
    this.tourSupported$ = this.store.select(UserPreferencesState.tourSupported);
  }

  ngOnInit(): void {
    this.isLoggedIn$
      .pipe(
        filter((auth) => !!auth),
        switchMap(() =>
          merge(
            fromEvent(document, 'keydown'),
            fromEvent(document, 'mousedown'),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((e) => this._handleSearchPopupTriggers(e));

    this.store
      .select(UserState.currentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userInfo = user;
      });

    this.store
      .dispatch(new DelegationActions.LoadActiveDelegationDetails())
      .pipe(
        switchMap(() =>
          this.store.select(DelegationState.activeDelegationDetails),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.activeDelegation = result;
      });

    this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobileScreen = result.matches;
      });
  }

  onPressed = () => {
    this.clickHamburger = !this.clickHamburger;
  };

  onSearchAnimationEnd(event: AnimationEvent): void {
    switch (event.toState) {
      case 'visible':
        this.searchInputViewChild.nativeElement.focus();
        break;
    }
  }

  signIn() {
    this.store.dispatch(new SignIn());
  }

  signOut() {
    this.store.dispatch(new SignOut());
  }

  toggleSearch(value = !this.showSearch): void {
    this.showSearch = value;
  }

  toggleNotifications() {
    this.showNotification = !this.showNotification;
  }

  onClickNotification() {
    this.showNotification = false;
  }

  startTour(): void {
    this.store.dispatch(new UserPreferencesActions.StartTours());
  }

  private _handleSearchPopupTriggers(e: Event) {
    // On Keydown:
    if (e instanceof KeyboardEvent) {
      if (e.code === 'F3' || (e.ctrlKey && e.code === 'KeyF')) {
        this.showSearch = true;
        e.preventDefault();
      }
    }
    // On Mousedown:
    else {
      // TODO: move the following logic to a directive.
      const target = e.target as HTMLElement;
      const parentNode = target?.parentNode as HTMLElement;
      const classList: DOMTokenList | null = parentNode?.classList || null;

      if (
        !classList?.toString().includes('search') &&
        !this.searchMenu?.nativeElement.contains(target)
      ) {
        this.showSearch = false;
      }

      // Close notification when click outside of notification
      if (
        !classList?.toString().includes('notification') &&
        !this.notificationMenu?.nativeElement.contains(target)
      ) {
        this.showNotification = false;
      }
    }
  }
  onTopbarAiButtonClick() {
    this.showAISide = !this.showAISide;
  }
}
